import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { UserSummary } from '@shared/types';
import { Card, CardContent, buttonVariants } from '@/components/ui';
import { FeatureLoading } from '@/features/_shared/FeatureUI';
import { apiFetch, getErrorMessage } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { routes } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

export function PurchaserClaimPage() {
  const { token = '' } = useParams();
  const started = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const claim = useMutation({
    mutationFn: () =>
      apiFetch<{ user: UserSummary }>('/api/auth/claim-purchaser', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(queryKeys.session, user);
      void navigate(routes.passwordSetup, { replace: true });
    },
  });
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    claim.mutate();
  }, [claim]);
  usePageMeta({
    title: 'Set up your purchaser account',
    description: 'Use your private setup link to choose a password.',
    noIndex: true,
  });
  if (claim.isPending || claim.isIdle) {
    return <FeatureLoading label="Securing your purchaser account" />;
  }
  if (!claim.isError) return null;
  return (
    <Card className="mx-auto max-w-lg">
      <CardContent className="p-7 text-center sm:p-9">
        <h1 className="text-2xl font-bold text-slate-950">This setup link cannot be used</h1>
        <p className="mt-3 leading-7 text-slate-600">
          {getErrorMessage(
            claim.error,
            'The link may be expired or already used. Ask the administrator to generate a new purchaser link.',
          )}
        </p>
        <Link className={buttonVariants({ className: 'mt-6' })} to={routes.login}>
          Go to sign in
        </Link>
      </CardContent>
    </Card>
  );
}

export default PurchaserClaimPage;
